// React Query hooks for tuition grid management

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as tuitionService from '@/services/tuitionService';
import type {
  TuitionGridQuery,
  CreateTuitionGridData,
  UpdateTuitionGridData,
  BulkCreateTuitionGridData,
  CreateTuitionGridSettingsData,
} from '@/types/tuition.types';

// ==================== Query Keys ====================

export const tuitionGridKeys = {
  all: ['tuition-grids'] as const,
  lists: () => [...tuitionGridKeys.all, 'list'] as const,
  list: (query: TuitionGridQuery) => [...tuitionGridKeys.lists(), query] as const,
  details: () => [...tuitionGridKeys.all, 'detail'] as const,
  detail: (id: string) => [...tuitionGridKeys.details(), id] as const,
  dashboard: () => [...tuitionGridKeys.all, 'dashboard'] as const,
  campusInfo: () => [...tuitionGridKeys.all, 'campus-info'] as const,
  filieres: () => [...tuitionGridKeys.all, 'filieres'] as const,
  filieresGrouped: () => [...tuitionGridKeys.all, 'filieres-grouped'] as const,
  settings: () => [...tuitionGridKeys.all, 'settings'] as const,
};

// ==================== Query Hooks ====================

/**
 * Fetch tuition grids with pagination and filtering
 */
export function useTuitionGrids(query: TuitionGridQuery) {
  return useQuery({
    queryKey: tuitionGridKeys.list(query),
    queryFn: () => tuitionService.fetchTuitionGrids(query),
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Fetch a single tuition grid by ID
 */
export function useTuitionGrid(id: string | null) {
  return useQuery({
    queryKey: tuitionGridKeys.detail(id || ''),
    queryFn: () => tuitionService.fetchTuitionGrid(id!),
    enabled: !!id,
  });
}

/**
 * Fetch dashboard summary (only filières with configured prices)
 */
export function useDashboardSummary() {
  return useQuery({
    queryKey: tuitionGridKeys.dashboard(),
    queryFn: () => tuitionService.fetchDashboardSummary(),
    staleTime: 30000,
  });
}

/**
 * Fetch campus information
 */
export function useCampusInfo() {
  return useQuery({
    queryKey: tuitionGridKeys.campusInfo(),
    queryFn: () => tuitionService.getCampusInfo(),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Fetch filières for tuition grid creation
 */
export function useFilieresForTuition() {
  return useQuery({
    queryKey: tuitionGridKeys.filieres(),
    queryFn: () => tuitionService.fetchFilieresForTuition(),
    staleTime: 60000, // 1 minute
  });
}

// ==================== Mutation Hooks ====================

/**
 * Sync filières from pedagogical system
 */
export function useSyncFilieres() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => tuitionService.syncFilieresFromPedagogical(),
    onSuccess: () => {
      // Invalidate all tuition grid queries
      queryClient.invalidateQueries({ queryKey: tuitionGridKeys.all });
    },
  });
}

/**
 * Create a single tuition grid
 */
export function useCreateTuitionGrid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTuitionGridData) => tuitionService.createTuitionGrid(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tuitionGridKeys.all });
    },
  });
}

/**
 * Bulk create tuition grids
 */
export function useCreateTuitionGridBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkCreateTuitionGridData) => tuitionService.createTuitionGridBulk(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tuitionGridKeys.all });
    },
  });
}

/**
 * Update a tuition grid
 */
export function useUpdateTuitionGrid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTuitionGridData }) =>
      tuitionService.updateTuitionGrid(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: tuitionGridKeys.all });
      queryClient.invalidateQueries({ queryKey: tuitionGridKeys.detail(id) });
    },
  });
}

/**
 * Archive a tuition grid
 */
export function useArchiveTuitionGrid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tuitionService.archiveTuitionGrid(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tuitionGridKeys.all });
    },
  });
}

/**
 * Restore an archived tuition grid
 */
export function useRestoreTuitionGrid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tuitionService.restoreTuitionGrid(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tuitionGridKeys.all });
    },
  });
}

// ==================== Settings Hooks ====================

/**
 * Fetch tuition grid settings for a tenant (not academic year specific)
 */
export function useTuitionGridSettings() {
  return useQuery({
    queryKey: tuitionGridKeys.settings(),
    queryFn: () => tuitionService.fetchTuitionGridSettings(),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Save tuition grid settings
 */
export function useSaveTuitionGridSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTuitionGridSettingsData) =>
      tuitionService.saveTuitionGridSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tuitionGridKeys.settings() });
      queryClient.invalidateQueries({ queryKey: tuitionGridKeys.all });
    },
  });
}

// ==================== Grouped Filières Hooks ====================

/**
 * Fetch filières grouped by parent for tuition grid display
 */
export function useFilieresGroupedForTuition() {
  return useQuery({
    queryKey: tuitionGridKeys.filieresGrouped(),
    queryFn: () => tuitionService.fetchFilieresGroupedForTuition(),
    staleTime: 60000, // 1 minute
  });
}

/**
 * Fetch tuition grids grouped for display
 */
export function useTuitionGridsGrouped(
  campusId?: string,
  includeArchived?: boolean
) {
  return useQuery({
    queryKey: [...tuitionGridKeys.lists(), 'grouped', campusId, includeArchived] as const,
    queryFn: () => tuitionService.fetchTuitionGridsGrouped(campusId, includeArchived),
    staleTime: 30000,
  });
}
